import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ApiProvider } from "@src/store/ApiProvider";
import { pricingActions } from "..";
import { RootState } from "@src/store/store";
import { EventInventoryRecord, getInventoryCacheEventId } from "@src/store/pricing/queryEventInventoryAllSlice";
import { handleError } from "@src/store/getThunkAndSlice";

export interface ICreateExternalInvoiceBulkParams {
	ListingIds: Array<number>;
	ExchangeName: string;
	ExchangeCustomerID: string;
	ZeroCost?: boolean;
	CompanyName?: string;
}

export interface ICreateExternalInvoiceBulkResponse {
	success: boolean;
	invoiceId: number;
}

export const createExternalInvoiceBulk = createAsyncThunk<
	ICreateExternalInvoiceBulkResponse,
	ICreateExternalInvoiceBulkParams
>("sales/createExternalInvoiceBulk", async (params, { dispatch, rejectWithValue, getState }) => {
	try {
		const rootState = getState() as RootState;
		const CompanyName = params.CompanyName ?? rootState.settings.companyName;

		const body: Record<string, unknown> = {
			CompanyName,
			ListingIds: params.ListingIds,
			ExchangeCustomerID: params.ExchangeCustomerID,
			ExchangeName: params.ExchangeName,
		};
		if (params.ZeroCost) body.ZeroCost = true;

		const response = await ApiProvider.default.post<ICreateExternalInvoiceBulkResponse>(
			"/api/Sales/CreateExternalInvoiceBulk",
			body,
		);

		const invoicedIds = new Set(params.ListingIds);
		const invoiceId = response?.invoiceId ?? 0;

		const patch = (item: EventInventoryRecord): EventInventoryRecord => {
			if (!invoicedIds.has(item.Listing_ID)) return item;
			return { ...item, Invoice_ID: invoiceId, Exchange_Name: params.ExchangeName };
		};

		const newData = rootState.pricing.eventInventoryAll.data.map(patch);
		dispatch(pricingActions.updateInventoryAll({ data: newData, eventId: getInventoryCacheEventId(rootState) }));

		if (rootState.pricing.globalInventory.data.length) {
			const newGlobalData = (rootState.pricing.globalInventory.data as Array<EventInventoryRecord>).map(patch);
			dispatch(pricingActions.updateGlobalInventory({ data: newGlobalData }));
		}

		return response;
	} catch (error) {
		return rejectWithValue(handleError(error));
	}
});

export const createExternalInvoiceBulkSlice = createSlice({
	name: "sales/createExternalInvoiceBulk",
	initialState: { loading: false, error: null as string | null },
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(createExternalInvoiceBulk.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(createExternalInvoiceBulk.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(createExternalInvoiceBulk.rejected, (state, action) => {
				state.loading = false;
				state.error = (action.payload as string) ?? null;
			});
	},
});
